import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Section } from '../../ReportUIPrimitives';
import styles from '../../MetaReportViewer.module.css';
import {
  AttributionBlock,
  CollapsibleSection,
  InlineAdPreview,
  VideoBlock,
} from './common';
import {
  analyzeShopMetaCampaign,
  getShopMetaCampaignAnalysis,
} from '../../../../utils/api/reportApi';
import {
  ADSET_BIDDING,
  ADSET_IDS,
  ADSET_TARGETING,
  AD_CREATIVE,
  AD_IDS,
  CAMPAIGN_IDS,
  CAMPAIGN_SETTINGS,
  aggregateRows,
  fmt,
  fmtMoney,
  resolveCurrency,
  roasColor,
} from './commonData';

const buildHierarchy = (windowMonths) => {
  const allCampaigns = windowMonths.flatMap((month) => month.campaigns || []);
  const allAdsets = windowMonths.flatMap((month) => month.adsets || []);
  const allAds = windowMonths.flatMap((month) => month.ads || []);

  const adsByAdset = new Map();
  allAds.forEach((ad) => {
    const adsetKey = ad.adset_id || ad.adset_name || '';
    const adKey = ad.ad_id || ad.ad_name || '';
    if (!adsByAdset.has(adsetKey)) adsByAdset.set(adsetKey, new Map());
    if (!adsByAdset.get(adsetKey).has(adKey)) adsByAdset.get(adsetKey).set(adKey, []);
    adsByAdset.get(adsetKey).get(adKey).push(ad);
  });

  const adsetsByCampaign = new Map();
  allAdsets.forEach((adset) => {
    const campaignKey = adset.campaign_id || adset.campaign_name || '';
    const adsetKey = adset.adset_id || adset.adset_name || '';
    if (!adsetsByCampaign.has(campaignKey)) adsetsByCampaign.set(campaignKey, new Map());
    if (!adsetsByCampaign.get(campaignKey).has(adsetKey)) adsetsByCampaign.get(campaignKey).set(adsetKey, []);
    adsetsByCampaign.get(campaignKey).get(adsetKey).push(adset);
  });

  const campaignMap = new Map();
  allCampaigns.forEach((campaign) => {
    const campaignKey = campaign.campaign_id || campaign.campaign_name || '';
    if (!campaignMap.has(campaignKey)) campaignMap.set(campaignKey, []);
    campaignMap.get(campaignKey).push(campaign);
  });

  return [...campaignMap.entries()].map(([campaignKey, campaignRows]) => {
    const adsetMap = adsetsByCampaign.get(campaignKey) || new Map();
    const adsets = [...adsetMap.entries()].map(([adsetKey, adsetRows]) => {
      const adMap = adsByAdset.get(adsetKey) || new Map();
      const ads = [...adMap.entries()].map(([adKey, adRows]) => ({ ...aggregateRows(adRows), _key: adKey }));
      return { ...aggregateRows(adsetRows), _key: adsetKey, ads };
    });
    return { ...aggregateRows(campaignRows), _key: campaignKey, adsets };
  });
};

const HRow = ({ level, name, badge, count, row, detail, children, autoOpen = false, isFocused = false, currency = null }) => {
  const [open, setOpen] = useState(autoOpen);
  const isOpen = autoOpen || open;
  const rowCurrency = resolveCurrency(row, currency);

  const metrics = [
    { label: 'Spend', value: fmtMoney(row.spend, rowCurrency) },
    { label: 'Impr', value: fmt(row.impressions, 0) },
    { label: 'Reach', value: fmt(row.reach, 0) },
    { label: 'Freq', value: row.frequency != null ? fmt(row.frequency) : '—' },
    { label: 'Clicks', value: fmt(row.clicks, 0) },
    { label: 'CTR', value: row.ctr != null ? `${fmt(row.ctr)}%` : '—' },
    { label: 'CPM', value: row.cpm != null ? fmtMoney(row.cpm, rowCurrency) : '—' },
    { label: 'CPC', value: row.cpc != null ? fmtMoney(row.cpc, rowCurrency) : '—' },
    { label: 'Conv', value: fmt(row.conversions, 0) },
    { label: 'ROAS', value: row.roas != null ? `${fmt(row.roas, 2)}×` : '—', roas: true },
  ];
  return (
    <div className={`${styles.hBlock} ${styles[`hLevel${level}`]} ${isFocused ? styles.hBlockFocused : ''}`}>
      <button className={styles.hHeader} onClick={() => setOpen((value) => !value)}>
        <span className={`${styles.hChevron} ${isOpen ? styles.hChevronOpen : ''}`}>▶</span>
        <div className={styles.hNameCol}>
          <span className={styles[`hName${level}`]}>{name}</span>
          <div className={styles.hNameMeta}>
            {badge && <span className={styles.hBadge}>{badge}</span>}
            {count != null && <span className={styles.hCount}>{count}</span>}
          </div>
          <div className={styles.hMetricsRow}>
            {metrics.map((metric) => (
              <div key={metric.label} className={`${styles.hMetric} ${metric.roas ? roasColor(row.roas) : ''}`}>
                <span className={styles.hMetricVal}>{metric.value}</span>
                <span className={styles.hMetricLbl}>{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
      </button>
      {isOpen && (
        <>
          <div className={styles.hDetail}>{detail}</div>
          {children && <div className={styles.hChildren}>{children}</div>}
        </>
      )}
    </div>
  );
};

const SORT_OPTIONS = [
  { key: 'spend', label: 'Spend' },
  { key: 'roas', label: 'ROAS' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'reach', label: 'Reach' },
  { key: 'frequency', label: 'Frequency' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'conversions', label: 'Conversions' },
  { key: 'ctr', label: 'CTR' },
  { key: 'cpm', label: 'CPM' },
  { key: 'cpc', label: 'CPC' },
];

const getName = (item) =>
  item.ad_name || item.adset_name || item.campaign_name ||
  item.ad_id || item.adset_id || item.campaign_id || '';

const applyControls = (items, search, sortKey, sortDir) => {
  let result = items;
  if (search.trim()) {
    const query = search.trim().toLowerCase();
    result = result.filter((item) => getName(item).toLowerCase().includes(query));
  }
  if (sortKey) {
    result = [...result].sort((a, b) => {
      const aValue = Number(a[sortKey]) || 0;
      const bValue = Number(b[sortKey]) || 0;
      return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }
  return result;
};

const matchesCampaignFocus = (campaign, focusCampaignId, focusCampaignName) => (
  (Boolean(focusCampaignId) && (campaign?.campaign_id || '') === focusCampaignId)
  || (Boolean(focusCampaignName) && (campaign?.campaign_name || '') === focusCampaignName)
);

const matchesAdsetFocus = (adset, focusAdsetId, focusAdsetName) => (
  (Boolean(focusAdsetId) && (adset?.adset_id || '') === focusAdsetId)
  || (Boolean(focusAdsetName) && (adset?.adset_name || '') === focusAdsetName)
);

const HierarchyControls = ({ search, setSearch, sortKey, setSortKey, sortDir, setSortDir, placeholder }) => (
  <div className={styles.hControls}>
    <div className={styles.hSearchWrap}>
      <span className={styles.hSearchIcon}>⌕</span>
      <input
        className={styles.hSearchInput}
        type="text"
        placeholder={placeholder || 'Search…'}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {search && (
        <button className={styles.hSearchClear} onClick={() => setSearch('')}>✕</button>
      )}
    </div>
    <div className={styles.hSortWrap}>
      <select
        className={styles.hSortSelect}
        value={sortKey}
        onChange={(event) => setSortKey(event.target.value)}
      >
        <option value="">Sort by…</option>
        {SORT_OPTIONS.map((option) => (
          <option key={option.key} value={option.key}>{option.label}</option>
        ))}
      </select>
      {sortKey && (
        <button
          className={styles.hSortDir}
          onClick={() => setSortDir((direction) => direction === 'desc' ? 'asc' : 'desc')}
          title={sortDir === 'desc' ? 'Highest first' : 'Lowest first'}
        >
          {sortDir === 'desc' ? '↓' : '↑'}
        </button>
      )}
    </div>
  </div>
);

const AdRow = ({ ad, currency = null }) => (
  <HRow
    level="Ad"
    name={ad.ad_name || ad.ad_id || 'Ad'}
    badge={ad.creative_type || null}
    row={ad}
    currency={currency}
    detail={(
      <>
        <VideoBlock row={ad} />
        <AttributionBlock row={ad} currency={currency} />
        <CollapsibleSection label="IDs" fields={AD_IDS} row={ad} defaultOpen currency={currency} />
        <CollapsibleSection label="Targeting" fields={ADSET_TARGETING} row={ad} defaultOpen currency={currency} />
        <CollapsibleSection label="Bidding" fields={ADSET_BIDDING} row={ad} defaultOpen currency={currency} />
        <CollapsibleSection label="Creative" fields={AD_CREATIVE} row={ad} currency={currency} />
        <InlineAdPreview row={ad} />
      </>
    )}
  />
);

function CampaignAnalysisPanel({
  campaign,
  userId,
  authToken,
  reportId,
  shopId,
  fromMonth,
  toMonth,
}) {
  const [assessment, setAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (!userId || !reportId) {
      setAssessment(null);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    setError('');

    getShopMetaCampaignAnalysis({
      userId,
      reportId,
      shopId,
      campaignId: campaign?.campaign_id,
      campaignName: campaign?.campaign_name,
      fromMonth,
      toMonth,
      authToken,
    })
      .then((result) => {
        if (cancelled) return;
        setAssessment(result?.assessment || null);
      })
      .catch(() => {
        if (cancelled) return;
        setAssessment(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    authToken,
    campaign?.campaign_id,
    campaign?.campaign_name,
    fromMonth,
    reportId,
    shopId,
    toMonth,
    userId,
  ]);

  const handleAnalyze = async () => {
    if (!userId) return;
    setIsAnalyzing(true);
    setError('');
    try {
      const result = await analyzeShopMetaCampaign({
        userId,
        reportId,
        shopId,
        campaignId: campaign?.campaign_id,
        campaignName: campaign?.campaign_name,
        fromMonth,
        toMonth,
        authToken,
      });
      setAssessment(result?.assessment || null);
    } catch (err) {
      setError(err?.message || 'Failed to analyze the selected campaign.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className={styles.matrixAssessmentCard}>
      <div className={styles.matrixAssessmentHeader}>
        <p className={styles.matrixEyebrow}>Campaign Analysis</p>
        {assessment ? (
          <span className={styles.matrixAssessmentLabel}>Analyzed</span>
        ) : null}
      </div>
      <div className={styles.matrixActionRow}>
        <button
          type="button"
          className={styles.matrixAnalyzeButton}
          onClick={handleAnalyze}
          disabled={isAnalyzing || !userId}
        >
          {isAnalyzing ? 'Analyzing…' : assessment ? 'Refresh Analysis' : 'AI Analysis'}
        </button>
        {isLoading ? <span className={styles.matrixSelectorMeta}>Loading saved analysis…</span> : null}
        {error ? <span className={styles.matrixActionError}>{error}</span> : null}
      </div>
      {assessment ? (
        <p className={styles.sampleAdPrimaryText}>{assessment}</p>
      ) : (
        <p className={styles.summaryText}>Run AI Analysis for this campaign and current time window.</p>
      )}
    </div>
  );
}

function AdsetRow({ adset, focusAdsetId, focusAdsetName, currency = null }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');

  const visibleAds = applyControls(adset.ads || [], search, sortKey, sortDir);
  const isFocused = matchesAdsetFocus(adset, focusAdsetId, focusAdsetName);

  return (
    <HRow
      level="Adset"
      name={adset.adset_name || adset.adset_id || 'Ad Set'}
      badge={[adset.geo_targeting, adset.age_range, adset.gender].filter(Boolean).join(' · ') || null}
      count={adset.ads?.length ? `${adset.ads.length} ad${adset.ads.length !== 1 ? 's' : ''}` : null}
      row={adset}
      currency={currency}
      autoOpen={isFocused}
      isFocused={isFocused}
      detail={(
        <>
          <AttributionBlock row={adset} currency={currency} />
          <CollapsibleSection label="IDs" fields={ADSET_IDS} row={adset} defaultOpen currency={currency} />
          <CollapsibleSection label="Targeting" fields={ADSET_TARGETING} row={adset} defaultOpen currency={currency} />
          <CollapsibleSection label="Bidding" fields={ADSET_BIDDING} row={adset} defaultOpen currency={currency} />
        </>
      )}
    >
      {(adset.ads || []).length > 0 && (
        <HierarchyControls
          search={search}
          setSearch={setSearch}
          sortKey={sortKey}
          setSortKey={setSortKey}
          sortDir={sortDir}
          setSortDir={setSortDir}
          placeholder="Search ads…"
        />
      )}
      {visibleAds.map((ad, index) => (
        <AdRow key={ad._key || index} ad={ad} currency={currency} />
      ))}
    </HRow>
  );
}

function CampaignRow({
  campaign,
  focusCampaignId,
  focusCampaignName,
  focusAdsetId,
  focusAdsetName,
  focusToken,
  currency = null,
}) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');

  const visibleAdsets = applyControls(campaign.adsets || [], search, sortKey, sortDir);
  const campaignMatches = matchesCampaignFocus(campaign, focusCampaignId, focusCampaignName);
  const containsFocusedAdset = (campaign.adsets || []).some((adset) => matchesAdsetFocus(adset, focusAdsetId, focusAdsetName));

  return (
    <HRow
      level="Campaign"
      name={campaign.campaign_name || campaign.campaign_id || 'Campaign'}
      badge={campaign.objective ? campaign.objective.replace('OUTCOME_', '') : null}
      count={campaign.adsets?.length ? `${campaign.adsets.length} ad set${campaign.adsets.length !== 1 ? 's' : ''}` : null}
      row={campaign}
      currency={currency}
      autoOpen={campaignMatches || containsFocusedAdset}
      detail={(
        <>
          <AttributionBlock row={campaign} currency={currency} />
          <CollapsibleSection label="IDs" fields={CAMPAIGN_IDS} row={campaign} defaultOpen currency={currency} />
          <CollapsibleSection label="Settings" fields={CAMPAIGN_SETTINGS} row={campaign} defaultOpen currency={currency} />
        </>
      )}
    >
      {(campaign.adsets || []).length > 0 && (
        <HierarchyControls
          search={search}
          setSearch={setSearch}
          sortKey={sortKey}
          setSortKey={setSortKey}
          sortDir={sortDir}
          setSortDir={setSortDir}
          placeholder="Search ad sets…"
        />
      )}
      {visibleAdsets.map((adset, index) => (
        <AdsetRow
          key={`${adset._key || index}:${matchesAdsetFocus(adset, focusAdsetId, focusAdsetName) ? focusToken : 'base'}`}
          adset={adset}
          focusAdsetId={focusAdsetId}
          focusAdsetName={focusAdsetName}
          currency={currency}
        />
      ))}
    </HRow>
  );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const toMonthKey = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const fmtPickerDate = (date) => {
  if (!date) return '';
  return `${MONTH_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const parseInputDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

const buildDatePresets = (monthKeys = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sub = (days) => {
    const date = new Date(today);
    date.setDate(date.getDate() - days);
    return date;
  };
  const startOfWeek = (date) => {
    const result = new Date(date);
    result.setDate(result.getDate() - result.getDay());
    return result;
  };
  const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const startOfYear = (date) => new Date(date.getFullYear(), 0, 1);

  const lastMonthStart = startOfMonth(sub(today.getDate()));
  const lastMonthEnd = endOfMonth(lastMonthStart);
  const lastWeekStart = startOfWeek(sub(7));
  const lastWeekEnd = new Date(lastWeekStart.getTime() + 6 * 86400000);
  const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
  const firstMonthKey = monthKeys[monthKeys.length - 1] || '';
  const lastMonthKey = monthKeys[0] || '';
  const allTimeFrom = firstMonthKey ? new Date(`${firstMonthKey}-01T00:00:00`) : null;
  const allTimeTo = lastMonthKey
    ? new Date(new Date(`${lastMonthKey}-01T00:00:00`).getFullYear(), new Date(`${lastMonthKey}-01T00:00:00`).getMonth() + 1, 0)
    : null;
  const lastYearStart = new Date(today.getFullYear() - 1, today.getMonth(), 1);

  return [
    { label: 'Custom', from: null, to: null, windowKey: null },
    { label: 'All time', from: allTimeFrom, to: allTimeTo, windowKey: null },
    { label: 'Today', from: today, to: today, windowKey: 'today' },
    { label: 'Yesterday', from: sub(1), to: sub(1), windowKey: 'yesterday' },
    { label: 'This week (Sun–Today)', from: startOfWeek(today), to: today, windowKey: 'this_week' },
    { label: 'Last 7 days', from: sub(6), to: today, windowKey: 'last_7_days' },
    { label: 'Last week (Sun–Sat)', from: lastWeekStart, to: lastWeekEnd, windowKey: 'last_week' },
    { label: 'Last 28 days', from: sub(27), to: today, windowKey: 'last_28_days' },
    { label: 'Last 30 days', from: sub(29), to: today, windowKey: 'last_30_days' },
    { label: 'This month', from: startOfMonth(today), to: today, windowKey: null },
    { label: 'Last month', from: lastMonthStart, to: lastMonthEnd, windowKey: null },
    { label: 'Last 90 days', from: sub(89), to: today, windowKey: null },
    { label: 'Last year', from: lastYearStart, to: today, windowKey: null },
    { label: 'Quarter to date', from: quarterStart, to: today, windowKey: null },
    { label: 'This year (Jan–Today)', from: startOfYear(today), to: today, windowKey: null },
    { label: 'Last calendar year', from: new Date(today.getFullYear() - 1, 0, 1), to: new Date(today.getFullYear() - 1, 11, 31), windowKey: null },
  ].filter((preset) => preset.label === 'Custom' || (preset.from && preset.to));
};

function CalendarGrid({ year, month, startDate, endDate, hoverDate, onDayClick, onDayHover }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells = [];
  for (let index = 0; index < firstDay; index += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));

  const rangeEnd = endDate || hoverDate;

  return (
    <div className={styles.calGrid}>
      <div className={styles.calMonthTitle}>{MONTH_NAMES[month]} {year}</div>
      <div className={styles.calDayLabels}>
        {DAY_LABELS.map((label) => <span key={label} className={styles.calDayLabel}>{label}</span>)}
      </div>
      <div className={styles.calDays}>
        {cells.map((day, index) => {
          if (!day) return <span key={`e${index}`} className={styles.calEmpty} />;
          const isStart = startDate && sameDay(day, startDate);
          const isEnd = rangeEnd && sameDay(day, rangeEnd);
          const inRange = startDate && rangeEnd && day > (startDate < rangeEnd ? startDate : rangeEnd) && day < (startDate < rangeEnd ? rangeEnd : startDate);
          const isToday = sameDay(day, today);
          const cls = [
            styles.calDay,
            isStart || isEnd ? styles.calDayEndpoint : '',
            inRange ? styles.calDayInRange : '',
            isToday ? styles.calDayToday : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={day.getDate()}
              className={cls}
              onClick={() => onDayClick(day)}
              onMouseEnter={() => onDayHover(day)}
              onMouseLeave={() => onDayHover(null)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// export function DateRangePicker({ monthKeys, fromMonth, toMonth, onChange }) {
//   const [open, setOpen] = useState(false);
//   const [tempStart, setTempStart] = useState(null);
//   const [tempEnd, setTempEnd] = useState(null);
//   const [hoverDate, setHoverDate] = useState(null);
//   const [calYear, setCalYear] = useState(new Date().getFullYear());
//   const [calMonth, setCalMonth] = useState(new Date().getMonth());
//   const [startInput, setStartInput] = useState('');
//   const [endInput, setEndInput] = useState('');
//   const [activePreset, setActivePreset] = useState('Last 30 days');
//   const containerRef = useRef(null);

//   const presets = useMemo(() => buildDatePresets(monthKeys), [monthKeys]);

//   const triggerLabel = useMemo(() => {
//     const match = presets.find((preset) => preset.from && preset.to && toMonthKey(preset.from) === fromMonth && toMonthKey(preset.to) === toMonth);
//     if (match) return match.label;
//     return fromMonth && toMonth ? `${fromMonth} – ${toMonth}` : 'Select range';
//   }, [fromMonth, toMonth, presets]);

//   const rightCalYear = calMonth === 11 ? calYear + 1 : calYear;
//   const rightCalMonth = calMonth === 11 ? 0 : calMonth + 1;

//   const openPicker = useCallback(() => {
//     const start = fromMonth ? new Date(`${fromMonth}-02`) : new Date();
//     const end = toMonth ? new Date(`${toMonth}-28`) : new Date();
//     start.setHours(0, 0, 0, 0);
//     end.setHours(0, 0, 0, 0);
//     setTempStart(start);
//     setTempEnd(end);
//     setStartInput(fmtPickerDate(start));
//     setEndInput(fmtPickerDate(end));
//     setCalYear(start.getFullYear());
//     setCalMonth(start.getMonth());
//     setOpen(true);
//   }, [fromMonth, toMonth]);

//   const handleDayClick = useCallback((day) => {
//     if (!tempStart || (tempStart && tempEnd)) {
//       setTempStart(day);
//       setTempEnd(null);
//       setStartInput(fmtPickerDate(day));
//       setEndInput('');
//       setActivePreset('Custom');
//     } else {
//       const [start, end] = day < tempStart ? [day, tempStart] : [tempStart, day];
//       setTempStart(start);
//       setTempEnd(end);
//       setStartInput(fmtPickerDate(start));
//       setEndInput(fmtPickerDate(end));
//       setActivePreset('Custom');
//     }
//   }, [tempStart, tempEnd]);

//   const handlePreset = useCallback((preset) => {
//     if (!preset.from) {
//       setActivePreset('Custom');
//       return;
//     }
//     setActivePreset(preset.label);
//     setTempStart(preset.from);
//     setTempEnd(preset.to);
//     setStartInput(fmtPickerDate(preset.from));
//     setEndInput(fmtPickerDate(preset.to));
//     setCalYear(preset.from.getFullYear());
//     setCalMonth(preset.from.getMonth());
//   }, []);

//   const fmtDate = (date) => {
//     if (!date) return null;
//     const y = date.getFullYear();
//     const m = String(date.getMonth() + 1).padStart(2, '0');
//     const d = String(date.getDate()).padStart(2, '0');
//     return `${y}-${m}-${d}`;
//   };

//   const handleApply = useCallback(() => {
//     if (tempStart && tempEnd) {
//       const fromKey = toMonthKey(tempStart);
//       const toKey = toMonthKey(tempEnd);
//       const clampedFromKey = monthKeys.includes(fromKey)
//         ? fromKey
//         : monthKeys.reduce((best, key) => (key >= fromKey && (best === null || key < best)) ? key : best, null) || monthKeys[monthKeys.length - 1];
//       const clampedToKey = monthKeys.includes(toKey)
//         ? toKey
//         : monthKeys.reduce((best, key) => (key <= toKey && (best === null || key > best)) ? key : best, null) || monthKeys[0];
//       const orderedFromKey = clampedFromKey <= clampedToKey ? clampedFromKey : clampedToKey;
//       const orderedToKey = clampedFromKey <= clampedToKey ? clampedToKey : clampedFromKey;
//       // Pass exact dates alongside month keys so callers can detect sub-monthly ranges
//       const orderedSince = clampedFromKey <= clampedToKey ? fmtDate(tempStart) : fmtDate(tempEnd);
//       const orderedUntil = clampedFromKey <= clampedToKey ? fmtDate(tempEnd) : fmtDate(tempStart);
//       onChange(orderedFromKey, orderedToKey, orderedSince, orderedUntil);
//     }
//     setOpen(false);
//   }, [tempStart, tempEnd, monthKeys, onChange]);

//   const handleStartInput = (value) => {
//     setStartInput(value);
//     const date = parseInputDate(value);
//     if (date) {
//       setTempStart(date);
//       setCalYear(date.getFullYear());
//       setCalMonth(date.getMonth());
//       setActivePreset('Custom');
//     }
//   };

//   const handleEndInput = (value) => {
//     setEndInput(value);
//     const date = parseInputDate(value);
//     if (date) {
//       setTempEnd(date);
//       setActivePreset('Custom');
//     }
//   };

//   const prevMonth = () => {
//     if (calMonth === 0) {
//       setCalYear((year) => year - 1);
//       setCalMonth(11);
//     } else {
//       setCalMonth((month) => month - 1);
//     }
//   };

//   const nextMonth = () => {
//     if (calMonth === 11) {
//       setCalYear((year) => year + 1);
//       setCalMonth(0);
//     } else {
//       setCalMonth((month) => month + 1);
//     }
//   };

//   useEffect(() => {
//     if (!open) return undefined;
//     const handler = (event) => {
//       if (containerRef.current && !containerRef.current.contains(event.target)) {
//         setOpen(false);
//       }
//     };
//     document.addEventListener('mousedown', handler);
//     return () => document.removeEventListener('mousedown', handler);
//   }, [open]);

//   return (
//     <div className={styles.drpWrap} ref={containerRef}>
//       <button className={styles.drpTrigger} onClick={open ? () => setOpen(false) : openPicker}>
//         <span className={styles.drpTriggerLabel}>{triggerLabel}</span>
//         <span className={styles.drpTriggerChevron}>▼</span>
//       </button>

//       {open && (
//         <div className={styles.drpDropdown}>
//           <div className={styles.drpPresetList}>
//             {presets.map((preset) => (
//               <button
//                 key={preset.label}
//                 className={`${styles.drpPresetItem} ${activePreset === preset.label ? styles.drpPresetItemActive : ''}`}
//                 onClick={() => handlePreset(preset)}
//               >
//                 {preset.label}
//               </button>
//             ))}
//           </div>

//           <div className={styles.drpCalPanel}>
//             <div className={styles.drpDateInputRow}>
//               <div className={styles.drpDateInputWrap}>
//                 <span className={styles.drpDateInputLabel}>Start date</span>
//                 <input
//                   className={styles.drpDateInput}
//                   value={startInput}
//                   onChange={(event) => handleStartInput(event.target.value)}
//                   placeholder="MMM D, YYYY"
//                 />
//               </div>
//               <span className={styles.drpDateInputSep}>–</span>
//               <div className={styles.drpDateInputWrap}>
//                 <span className={styles.drpDateInputLabel}>End date</span>
//                 <input
//                   className={styles.drpDateInput}
//                   value={endInput}
//                   onChange={(event) => handleEndInput(event.target.value)}
//                   placeholder="MMM D, YYYY"
//                 />
//               </div>
//             </div>

//             <div className={styles.drpCalRow}>
//               <div className={styles.drpCalMonthWrap}>
//                 <button className={styles.drpCalNav} onClick={prevMonth}>‹</button>
//                 <CalendarGrid
//                   year={calYear}
//                   month={calMonth}
//                   startDate={tempStart}
//                   endDate={tempEnd}
//                   hoverDate={hoverDate}
//                   onDayClick={handleDayClick}
//                   onDayHover={setHoverDate}
//                 />
//               </div>
//               <div className={styles.drpCalMonthWrap}>
//                 <CalendarGrid
//                   year={rightCalYear}
//                   month={rightCalMonth}
//                   startDate={tempStart}
//                   endDate={tempEnd}
//                   hoverDate={hoverDate}
//                   onDayClick={handleDayClick}
//                   onDayHover={setHoverDate}
//                 />
//                 <button className={styles.drpCalNav} onClick={nextMonth}>›</button>
//               </div>
//             </div>

//             <div className={styles.drpActions}>
//               <button className={styles.drpCancel} onClick={() => setOpen(false)}>Cancel</button>
//               <button className={styles.drpApply} onClick={handleApply} disabled={!tempStart || !tempEnd}>Apply</button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

export function DateRangePicker({ monthKeys, fromMonth, toMonth, since, until, onChange }) {
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(null);
  const [tempEnd, setTempEnd] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [activePreset, setActivePreset] = useState('Last 30 days');
  const [activeWindowKey, setActiveWindowKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const presets = useMemo(() => buildDatePresets(monthKeys), [monthKeys]);

  const fmtDateKey = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const triggerLabel = useMemo(() => {
    const match = presets.find((preset) =>
      preset.from &&
      preset.to &&
      fmtDateKey(preset.from) === since &&
      fmtDateKey(preset.to) === until
    );

    if (match) return match.label;
    if (since && until) return `${since} – ${until}`;
    if (fromMonth && toMonth) return `${fromMonth} – ${toMonth}`;
    return 'Select range';
  }, [fromMonth, toMonth, since, until, presets]);

  const rightCalYear = calMonth === 11 ? calYear + 1 : calYear;
  const rightCalMonth = calMonth === 11 ? 0 : calMonth + 1;

  const openPicker = useCallback(() => {
    const start = since ? new Date(`${since}T00:00:00`) : (fromMonth ? new Date(`${fromMonth}-01T00:00:00`) : new Date());
    const end = until ? new Date(`${until}T00:00:00`) : (toMonth ? new Date(`${toMonth}-01T00:00:00`) : new Date());

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    setTempStart(start);
    setTempEnd(end);
    setStartInput(fmtPickerDate(start));
    setEndInput(fmtPickerDate(end));
    setCalYear(start.getFullYear());
    setCalMonth(start.getMonth());

    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 640;
      const left = Math.min(rect.left, window.innerWidth - dropdownWidth - 8);
      setDropdownPos({ top: rect.bottom + 6, left: Math.max(8, left) });
    }

    setOpen(true);
  }, [fromMonth, toMonth, since, until]);

  const handleDayClick = useCallback((day) => {
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(day);
      setTempEnd(null);
      setStartInput(fmtPickerDate(day));
      setEndInput('');
      setActivePreset('Custom');
      setActiveWindowKey(null);
    } else {
      const [start, end] = day < tempStart ? [day, tempStart] : [tempStart, day];
      setTempStart(start);
      setTempEnd(end);
      setStartInput(fmtPickerDate(start));
      setEndInput(fmtPickerDate(end));
      setActivePreset('Custom');
      setActiveWindowKey(null);
    }
  }, [tempStart, tempEnd]);

  const handlePreset = useCallback((preset) => {
    if (!preset.from) {
      setActivePreset('Custom');
      setActiveWindowKey(null);
      return;
    }
    setActivePreset(preset.label);
    setActiveWindowKey(preset.windowKey || null);
    setTempStart(preset.from);
    setTempEnd(preset.to);
    setStartInput(fmtPickerDate(preset.from));
    setEndInput(fmtPickerDate(preset.to));
    setCalYear(preset.from.getFullYear());
    setCalMonth(preset.from.getMonth());
  }, []);

  const fmtDate = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleApply = useCallback(() => {
    if (tempStart && tempEnd) {
      const fromKey = toMonthKey(tempStart);
      const toKey = toMonthKey(tempEnd);

      const clampedFromKey = monthKeys.includes(fromKey)
        ? fromKey
        : monthKeys.reduce((best, key) => (key >= fromKey && (best === null || key < best)) ? key : best, null) || monthKeys[monthKeys.length - 1];

      const clampedToKey = monthKeys.includes(toKey)
        ? toKey
        : monthKeys.reduce((best, key) => (key <= toKey && (best === null || key > best)) ? key : best, null) || monthKeys[0];

      const orderedFromKey = clampedFromKey <= clampedToKey ? clampedFromKey : clampedToKey;
      const orderedToKey = clampedFromKey <= clampedToKey ? clampedToKey : clampedFromKey;
      const orderedSince = clampedFromKey <= clampedToKey ? fmtDate(tempStart) : fmtDate(tempEnd);
      const orderedUntil = clampedFromKey <= clampedToKey ? fmtDate(tempEnd) : fmtDate(tempStart);

      onChange(orderedFromKey, orderedToKey, orderedSince, orderedUntil, activeWindowKey || null);
    }
    setOpen(false);
  }, [tempStart, tempEnd, monthKeys, onChange, activeWindowKey]);

  const handleStartInput = (value) => {
    setStartInput(value);
    const date = parseInputDate(value);
    if (date) {
      setTempStart(date);
      setCalYear(date.getFullYear());
      setCalMonth(date.getMonth());
      setActivePreset('Custom');
    }
  };

  const handleEndInput = (value) => {
    setEndInput(value);
    const date = parseInputDate(value);
    if (date) {
      setTempEnd(date);
      setActivePreset('Custom');
    }
  };

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalYear((year) => year - 1);
      setCalMonth(11);
    } else {
      setCalMonth((month) => month - 1);
    }
  };

  const nextMonth = () => {
    if (calMonth === 11) {
      setCalYear((year) => year + 1);
      setCalMonth(0);
    } else {
      setCalMonth((month) => month + 1);
    }
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      const inTrigger = containerRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className={styles.drpWrap} ref={containerRef}>
      <button ref={triggerRef} className={styles.drpTrigger} onClick={open ? () => setOpen(false) : openPicker}>
        <span className={styles.drpTriggerLabel}>{triggerLabel}</span>
        <span className={styles.drpTriggerChevron}>▼</span>
      </button>

      {open && createPortal(
        <div className={styles.drpPortalRoot}>
        <div ref={dropdownRef} className={styles.drpDropdown} style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}>
          <div className={styles.drpPresetList}>
            {presets.map((preset) => (
              <button
                key={preset.label}
                className={`${styles.drpPresetItem} ${activePreset === preset.label ? styles.drpPresetItemActive : ''}`}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className={styles.drpCalPanel}>
            <div className={styles.drpDateInputRow}>
              <div className={styles.drpDateInputWrap}>
                <span className={styles.drpDateInputLabel}>Start date</span>
                <input
                  className={styles.drpDateInput}
                  value={startInput}
                  onChange={(event) => handleStartInput(event.target.value)}
                  placeholder="MMM D, YYYY"
                />
              </div>
              <span className={styles.drpDateInputSep}>–</span>
              <div className={styles.drpDateInputWrap}>
                <span className={styles.drpDateInputLabel}>End date</span>
                <input
                  className={styles.drpDateInput}
                  value={endInput}
                  onChange={(event) => handleEndInput(event.target.value)}
                  placeholder="MMM D, YYYY"
                />
              </div>
            </div>

            <div className={styles.drpCalRow}>
              <div className={styles.drpCalMonthWrap}>
                <button className={styles.drpCalNav} onClick={prevMonth}>‹</button>
                <CalendarGrid
                  year={calYear}
                  month={calMonth}
                  startDate={tempStart}
                  endDate={tempEnd}
                  hoverDate={hoverDate}
                  onDayClick={handleDayClick}
                  onDayHover={setHoverDate}
                />
              </div>
              <div className={styles.drpCalMonthWrap}>
                <CalendarGrid
                  year={rightCalYear}
                  month={rightCalMonth}
                  startDate={tempStart}
                  endDate={tempEnd}
                  hoverDate={hoverDate}
                  onDayClick={handleDayClick}
                  onDayHover={setHoverDate}
                />
                <button className={styles.drpCalNav} onClick={nextMonth}>›</button>
              </div>
            </div>

            <div className={styles.drpActions}>
              <button className={styles.drpCancel} onClick={() => setOpen(false)}>Cancel</button>
              <button className={styles.drpApply} onClick={handleApply} disabled={!tempStart || !tempEnd}>Apply</button>
            </div>
          </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// const CampaignPerformanceExplorer = ({
//   months = [],
//   userId = '',
//   authToken = '',
//   reportId = '',
//   shopId = '',
//   fromMonth,
//   toMonth,
//   onRangeChange,
//   focusCampaignId = '',
//   focusCampaignName = '',
//   focusAdsetId = '',
//   focusAdsetName = '',
//   focusToken = 0,
// }) => {
  const CampaignPerformanceExplorer = ({
  months = [],
  userId = '',
  authToken = '',
  reportId = '',
  shopId = '',
  fromMonth: fromMonthProp,
  toMonth: toMonthProp,
  since: sinceProp,
  until: untilProp,
  onRangeChange,
  focusCampaignId = '',
  focusCampaignName = '',
  focusAdsetId = '',
  focusAdsetName = '',
  focusToken = 0,
  currency = null,
}) => {
  const sortedMonths = [...months].sort((a, b) => String(b.month).localeCompare(String(a.month)));
  const monthKeys = sortedMonths.map((month) => month.month);
  const [campSearch, setCampSearch] = useState('');
  const [campSortKey, setCampSortKey] = useState('spend');
  const [campSortDir, setCampSortDir] = useState('desc');
  const [internalFromMonth, setInternalFromMonth] = useState('');
  const [internalToMonth, setInternalToMonth] = useState('');
  const [internalSince, setInternalSince] = useState(null);
  const [internalUntil, setInternalUntil] = useState(null);

  if (!sortedMonths.length) return null;

  const isControlled = fromMonthProp !== undefined;
  const effectiveFromMonth = isControlled ? fromMonthProp : (internalFromMonth || monthKeys[monthKeys.length - 1] || '');
  const effectiveToMonth = isControlled ? toMonthProp : (internalToMonth || monthKeys[0] || '');
  const effectiveSince = isControlled ? sinceProp : internalSince;
  const effectiveUntil = isControlled ? untilProp : internalUntil;

  const handleRangeChange = (from, to, s, u, windowKey) => {
    if (!isControlled) {
      setInternalFromMonth(from);
      setInternalToMonth(to);
      setInternalSince(s || null);
      setInternalUntil(u || null);
    }
    onRangeChange?.(from, to, s, u, windowKey);
  };

  const windowMonths = sortedMonths.filter((month) => month.month >= effectiveFromMonth && month.month <= effectiveToMonth);
  const hierarchy = buildHierarchy(windowMonths);
  const visibleCampaigns = applyControls(hierarchy, campSearch, campSortKey, campSortDir);

  return (
    <Section title="Campaign Performance">
      <div className={styles.explorerControls}>
<DateRangePicker
  monthKeys={monthKeys}
  fromMonth={effectiveFromMonth}
  toMonth={effectiveToMonth}
  since={effectiveSince}
  until={effectiveUntil}
  onChange={handleRangeChange}
/>
        {/* <span className={styles.windowSummary}>
          {windowMonths.length} month{windowMonths.length !== 1 ? 's' : ''}
        </span> */}
      </div>

      <div className={styles.hColHeaderRow}>
        <div className={styles.hColHeader}>
          <span className={styles.hColName}>Name</span>
          <div className={styles.hMetricsScroll} />
        </div>
        {hierarchy.length > 0 && (
          <HierarchyControls
            search={campSearch}
            setSearch={setCampSearch}
            sortKey={campSortKey}
            setSortKey={setCampSortKey}
            sortDir={campSortDir}
            setSortDir={setCampSortDir}
            placeholder="Search campaigns…"
          />
        )}
      </div>

      {hierarchy.length === 0 ? (
        <div className={styles.explorerEmpty}>No data for {effectiveFromMonth} – {effectiveToMonth}</div>
      ) : (
        <div className={styles.hTable}>
          {visibleCampaigns.map((campaign, index) => (
            <CampaignRow
              key={`${campaign._key || index}:${(matchesCampaignFocus(campaign, focusCampaignId, focusCampaignName) || (campaign.adsets || []).some((adset) => matchesAdsetFocus(adset, focusAdsetId, focusAdsetName))) ? focusToken : 'base'}`}
              campaign={campaign}
              focusCampaignId={focusCampaignId}
              focusCampaignName={focusCampaignName}
              focusAdsetId={focusAdsetId}
              focusAdsetName={focusAdsetName}
              focusToken={focusToken}
              currency={currency}
            />
          ))}
        </div>
      )}
    </Section>
  );
};

export default CampaignPerformanceExplorer;
